import { useEffect, useState } from "react";
import API from "../../api/api";
import Modal from "../../components/Modal";
import Toast from "../../components/Toast";
import FormInput from "../../components/FormInput";

function MembershipList() {
    const [list, setList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [toast, setToast] = useState(null);
    const [membershipTypes, setMembershipTypes] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [viewMember, setViewMember] = useState(null);
    const [activeTab, setActiveTab] = useState("all"); // 'all', 'annual', 'life'

    const [formData, setFormData] = useState({
        membershipId: null,
        membershipTypeId: "",
        firstName: "",
        lastName: "",
        mobile: "",
        email: "",
        address: "",
        spouseName: "",
        child1: "",
        child2: "",
        child3: "",
        child4: "",
        donationAmount: 0
    });

    const fetchMemberships = () => {
        setLoading(true);
        API.get("/membership")
            .then(res => {
                setList(res.data);
                applyFilters(res.data, searchTerm, activeTab);
            })
            .catch(err => {
                console.error("Error fetching memberships:", err);
                setList([]);
            })
            .finally(() => setLoading(false));
    };

    const fetchMembershipTypes = async () => {
        try {
            const res = await API.get("/membership-types");
            setMembershipTypes(res.data);
        } catch (err) {
            console.error("Error fetching membership types:", err);
        }
    };

    useEffect(() => {
        fetchMemberships();
        fetchMembershipTypes();
    }, []);

    const applyFilters = (data, search, tab) => {
        let results = data;

        // Search filter
        if (search) {
            const lowSearch = search.toLowerCase();
            results = results.filter(x =>
                `${x.FirstName} ${x.LastName}`.toLowerCase().includes(lowSearch) ||
                x.Email?.toLowerCase().includes(lowSearch) ||
                x.Mobile?.includes(search)
            );
        }

        // Tab filter
        if (tab === "annual") {
            results = results.filter(x => x.MembershipTypeId === 1);
        } else if (tab === "life") {
            results = results.filter(x => x.MembershipTypeId === 2);
        }

        setFilteredList(results);
    };

    useEffect(() => {
        applyFilters(list, searchTerm, activeTab);
    }, [searchTerm, list, activeTab]);

    const handleOpenModal = (member = null) => {
        if (member) {
            setIsEditMode(true);
            setFormData({
                membershipId: member.MembershipId,
                membershipTypeId: member.MembershipTypeId,
                firstName: member.FirstName,
                lastName: member.LastName,
                mobile: member.Mobile,
                email: member.Email,
                address: member.Address,
                spouseName: member.SpouseName || "",
                child1: member.Child1 || "",
                child2: member.Child2 || "",
                child3: member.Child3 || "",
                child4: member.Child4 || "",
                donationAmount: member.DonationAmount
            });
        } else {
            setIsEditMode(false);
            setFormData({
                membershipId: null,
                membershipTypeId: "",
                firstName: "",
                lastName: "",
                mobile: "",
                email: "",
                address: "",
                spouseName: "",
                child1: "",
                child2: "",
                child3: "",
                child4: "",
                donationAmount: 0
            });
        }
        setIsModalOpen(true);
    };

    const handleView = (member) => {
        setViewMember(member);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const params = new URLSearchParams(formData).toString();
            if (isEditMode) {
                await API.put(`/membership/${formData.membershipId}?${params}`);
                showToast("Membership updated successfully");
            } else {
                await API.post(`/membership?${params}`);
                showToast("Membership created successfully");
            }
            setIsModalOpen(false);
            fetchMemberships();
        } catch (err) {
            console.error("Error saving membership:", err);
            showToast("Error saving membership", "error");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this membership record? This action cannot be undone.")) {
            try {
                console.log(`Attempting to delete membership with ID: ${id}`);
                const response = await API.delete(`/membership/${id}`);
                console.log("Delete response:", response.data);
                showToast("Record deleted successfully");
                fetchMemberships();
            } catch (err) {
                console.error("Error deleting record:", err);
                const errorMsg = err.response?.data || err.message || "Unknown error";
                showToast(`Error deleting record: ${errorMsg}`, "error");
            }
        }
    };

    const handleExport = () => {
        if (filteredList.length === 0) {
            showToast("No data to export", "error");
            return;
        }

        const headers = [
            "ID", "First Name", "Last Name", "Membership Type", 
            "Email", "Mobile", "Address", "Spouse Name", 
            "Child 1", "Child 2", "Child 3", "Child 4", 
            "Total Amount", "Donation Amount", "Status"
        ];

        const csvContent = [
            headers.join(","),
            ...filteredList.map(item => {
                const typeName = membershipTypes.find(t => t.MembershipTypeId === item.MembershipTypeId)?.TypeName || item.MembershipTypeName || 'N/A';
                return [
                    item.MembershipId,
                    `"${(item.FirstName || '').replace(/"/g, '""')}"`,
                    `"${(item.LastName || '').replace(/"/g, '""')}"`,
                    `"${typeName}"`,
                    `"${item.Email || ''}"`,
                    `"${item.Mobile || ''}"`,
                    `"${(item.Address || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`,
                    `"${(item.SpouseName || '').replace(/"/g, '""')}"`,
                    `"${(item.Child1 || '').replace(/"/g, '""')}"`,
                    `"${(item.Child2 || '').replace(/"/g, '""')}"`,
                    `"${(item.Child3 || '').replace(/"/g, '""')}"`,
                    `"${(item.Child4 || '').replace(/"/g, '""')}"`,
                    item.TotalAmount,
                    item.DonationAmount,
                    `"${item.PaymentStatus || 'PENDING'}"`
                ].join(",");
            })
        ].join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Membership_List_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast("Exported successfully");
    };

    const showToast = (message, type = "success") => {
        setToast({ message, type });
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'paid': return { bg: '#dcfce7', text: '#15803d' };
            case 'pending': return { bg: '#fef9c3', text: '#a16207' };
            case 'failed': return { bg: '#fee2e2', text: '#b91c1c' };
            default: return { bg: '#f1f5f9', text: '#475569' };
        }
    };

    return (
        <div className="admin-card animate-fade-in">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px", flexWrap: "wrap", gap: "20px" }}>
                <div>
                    <h2 style={{ margin: 0, color: "var(--text-dark)", fontSize: "1.75rem" }}>Membership Applications</h2>
                    <p style={{ margin: "5px 0 0 0", color: "var(--text-muted)", fontSize: "0.9rem" }}>Manage and review all community membership applications</p>
                </div>
                <div style={{ display: "flex", gap: "15px", alignItems: "center", flex: 1, justifyContent: "flex-end" }}>
                    <div className="search-container">
                        <span className="search-icon">🔍</span>
                        <input
                            className="form-control"
                            placeholder="Search by name, email, mobile..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button className="btn btn-outline" onClick={handleExport} style={{ height: "45px", display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "1.2rem" }}>📥</span> Export CSV
                    </button>
                    <button className="btn btn-primary" onClick={() => handleOpenModal()} style={{ height: "45px" }}>
                        <span>+</span> Create New
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ 
                display: "flex", 
                gap: "8px", 
                marginBottom: "24px", 
                borderBottom: "1px solid var(--border)",
                paddingBottom: "12px"
            }}>
                <button 
                    onClick={() => setActiveTab("all")}
                    className={`btn ${activeTab === "all" ? "btn-primary" : "btn-outline"}`}
                    style={{ padding: "8px 20px", borderRadius: "20px" }}
                >
                    All Memberships
                </button>
                <button 
                    onClick={() => setActiveTab("annual")}
                    className={`btn ${activeTab === "annual" ? "btn-primary" : "btn-outline"}`}
                    style={{ padding: "8px 20px", borderRadius: "20px" }}
                >
                    Annual Membership
                </button>
                <button 
                    onClick={() => setActiveTab("life")}
                    className={`btn ${activeTab === "life" ? "btn-primary" : "btn-outline"}`}
                    style={{ padding: "8px 20px", borderRadius: "20px" }}
                >
                    Life Membership
                </button>
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: "100px" }}>
                    <div className="spinner"></div>
                    <p style={{ marginTop: "15px", color: "var(--text-muted)" }}>Fetching memberships...</p>
                </div>
            ) : (
                <div className="table-responsive" style={{ overflowX: "auto" }}>
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Contact</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th style={{ textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredList.map(x => {
                                const status = getStatusColor(x.PaymentStatus);
                                const typeName = membershipTypes.find(t => t.MembershipTypeId === x.MembershipTypeId)?.TypeName || x.MembershipTypeName || 'N/A';
                                
                                return (
                                    <tr key={x.MembershipId}>
                                        <td style={{ fontWeight: "600", color: "var(--text-muted)", fontSize: "0.85rem" }}>#{x.MembershipId}</td>
                                        <td>
                                            <div style={{ fontWeight: "600" }}>{x.FirstName} {x.LastName}</div>
                                            <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{x.Email}</div>
                                        </td>
                                        <td>
                                            <span className="badge" style={{ background: "var(--primary-light)", color: "#fff" }}>
                                                {typeName}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ fontSize: "0.9rem" }}>{x.Mobile}</div>
                                            <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.Address}</div>
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: "700", color: "var(--primary)" }}>${x.TotalAmount}</div>
                                            {x.DonationAmount > 0 && <div style={{ fontSize: "0.75rem", color: "var(--success)" }}>incl. ${x.DonationAmount} donation</div>}
                                        </td>
                                        <td>
                                            <span className="badge" style={{ backgroundColor: status.bg, color: status.text }}>
                                                {x.PaymentStatus || 'PENDING'}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                                                <button 
                                                    className="btn btn-outline" 
                                                    style={{ padding: "6px", width: "36px", height: "36px" }} 
                                                    title="View Details"
                                                    onClick={() => handleView(x)}
                                                >
                                                    👁️
                                                </button>
                                                <button 
                                                    className="btn btn-outline" 
                                                    style={{ padding: "6px", width: "36px", height: "36px" }} 
                                                    title="Edit Member"
                                                    onClick={() => handleOpenModal(x)}
                                                >
                                                    ✏️
                                                </button>
                                                <button 
                                                    className="btn btn-outline" 
                                                    style={{ padding: "6px", width: "36px", height: "36px", color: "var(--danger)" }} 
                                                    title="Delete Member"
                                                    onClick={() => handleDelete(x.MembershipId)}
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {filteredList.length === 0 && !loading && (
                <div style={{ textAlign: "center", padding: "60px", color: "var(--text-muted)" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "10px" }}>📭</div>
                    <p>No membership applications found for the selected filter.</p>
                </div>
            )}

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            {/* Create/Edit Member Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={isEditMode ? "Edit Membership Details" : "Create New Membership"}
            >
                <form onSubmit={handleSubmit} className="premium-form">
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                        <div className="form-group">
                            <label>Membership Type</label>
                            <select
                                className="form-control"
                                value={formData.membershipTypeId}
                                onChange={e => setFormData({ ...formData, membershipTypeId: e.target.value })}
                                required
                                style={{ padding: '10px' }}
                            >
                                <option value="">Select Type</option>
                                {membershipTypes.map(t => (
                                    <option key={t.MembershipTypeId} value={t.MembershipTypeId}>{t.TypeName}</option>
                                ))}
                            </select>
                        </div>
                        <FormInput label="First Name" value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })} required />
                        <FormInput label="Last Name" value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })} required />
                        <FormInput label="Mobile" value={formData.mobile} onChange={e => setFormData({ ...formData, mobile: e.target.value })} required />
                        <FormInput label="Email" type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
                        <FormInput label="Address" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} required />
                        <FormInput label="Spouse Name" value={formData.spouseName} onChange={e => setFormData({ ...formData, spouseName: e.target.value })} />
                        <FormInput label="Child 1" value={formData.child1} onChange={e => setFormData({ ...formData, child1: e.target.value })} />
                        <FormInput label="Child 2" value={formData.child2} onChange={e => setFormData({ ...formData, child2: e.target.value })} />
                        <FormInput label="Child 3" value={formData.child3} onChange={e => setFormData({ ...formData, child3: e.target.value })} />
                        <FormInput label="Child 4" value={formData.child4} onChange={e => setFormData({ ...formData, child4: e.target.value })} />
                        <FormInput label="Donation Amount" type="number" value={formData.donationAmount} onChange={e => setFormData({ ...formData, donationAmount: parseFloat(e.target.value) || 0 })} />
                    </div>

                    <div style={{ marginTop: "30px", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                        <button type="button" className="btn btn-outline" onClick={() => setIsModalOpen(false)}>Cancel</button>
                        <button type="submit" className="btn btn-primary">{isEditMode ? "Update Membership" : "Create Membership"}</button>
                    </div>
                </form>
            </Modal>

            {/* View Member Details Modal */}
            <Modal
                isOpen={!!viewMember}
                onClose={() => setViewMember(null)}
                title="Member Details"
            >
                {viewMember && (
                    <div style={{ padding: "10px" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "20px" }}>
                            <div>
                                <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: "700" }}>Full Name</label>
                                <div style={{ fontSize: "1.1rem", fontWeight: "600" }}>{viewMember.FirstName} {viewMember.LastName}</div>
                            </div>
                            <div>
                                <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: "700" }}>Membership Type</label>
                                <div>
                                    <span className="badge" style={{ background: "var(--primary-light)", color: "#fff" }}>
                                        {membershipTypes.find(t => t.MembershipTypeId === viewMember.MembershipTypeId)?.TypeName || 'N/A'}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: "700" }}>Contact Info</label>
                                <div>📱 {viewMember.Mobile}</div>
                                <div>📧 {viewMember.Email}</div>
                            </div>
                            <div>
                                <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: "700" }}>Payment Status</label>
                                <div>
                                    <span className="badge" style={{ 
                                        backgroundColor: getStatusColor(viewMember.PaymentStatus).bg, 
                                        color: getStatusColor(viewMember.PaymentStatus).text 
                                    }}>
                                        {viewMember.PaymentStatus || 'PENDING'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: "20px" }}>
                            <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: "700" }}>Address</label>
                            <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid var(--border)" }}>
                                {viewMember.Address}
                            </div>
                        </div>

                        <div style={{ background: "#f8fafc", padding: "20px", borderRadius: "12px", marginBottom: "20px" }}>
                            <h4 style={{ margin: "0 0 15px 0", fontSize: "0.9rem" }}>Family Details</h4>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                                <div>
                                    <label style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>Spouse</label>
                                    <div style={{ fontWeight: "600" }}>{viewMember.SpouseName || "N/A"}</div>
                                </div>
                                <div>
                                    <label style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>Children</label>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                                        {[viewMember.Child1, viewMember.Child2, viewMember.Child3, viewMember.Child4].filter(Boolean).map((child, i) => (
                                            <span key={i} className="child-tag">👶 {child}</span>
                                        )) || "N/A"}
                                        {![viewMember.Child1, viewMember.Child2, viewMember.Child3, viewMember.Child4].filter(Boolean).length && "None"}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--primary)", color: "#fff", padding: "15px 20px", borderRadius: "12px" }}>
                            <div>
                                <div style={{ fontSize: "0.8rem", opacity: 0.8 }}>Total Amount</div>
                                <div style={{ fontSize: "1.5rem", fontWeight: "800" }}>${viewMember.TotalAmount}</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                                <div style={{ fontSize: "0.8rem", opacity: 0.8 }}>Donation</div>
                                <div style={{ fontSize: "1.1rem", fontWeight: "600" }}>${viewMember.DonationAmount}</div>
                            </div>
                        </div>

                        <div style={{ marginTop: "30px", display: "flex", justifyContent: "flex-end" }}>
                            <button className="btn btn-primary" onClick={() => setViewMember(null)}>Close</button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default MembershipList;